import {ChangeDetectionStrategy, ChangeDetectorRef, Component,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FormFieldConfig} from "../../../../model/api/form-field-config";
import {ClearingFlexService} from "../../../../services/clearing/flex/clearing-flex.service";
import {Owner} from "../../../../model/customer/owner";
import {Trade} from "../../../../model/clearing/trade";
import {CommonFieldService} from "../../../../services/common/common-field.service";
import {FlexTrade} from "../../../../model/clearing/flex/flex-trade";
import {MaturityUtils} from "../../../../utils/maturity-utils";
import {AppConfigService} from "../../../../services/app-config.service";
import {BatchUpdateRequest} from "../../../../model/api/batch-update-request";
import {DateTime} from "luxon";
import {ClearingTradeInfoService} from "../../../../services/clearing/clearing-trade-info.service";
import {FlexFormDialogBaseComponent} from "../flex-form-dialog-base.component";
import {Account} from "../../../../model/clearing/account";
import TYPE = FormFieldConfig.TYPE;
import Side = Trade.Side;
import {DateFormatPipe} from "../../../../utils/date-format.pipe";
import {FieldsService} from "../../../../services/common/fields-service";
import {
  ClearingFlexTradeDataLoadingService
} from "../../../../services/clearing/flex/clearing-flex-trade-data-loading.service";
import {BatchUtils} from "../../../../utils/batch-utils";

@Component({
  selector: 'app-flex-batch-edit-form-dialog',
  templateUrl: './flex-batch-edit-form-dialog.component.html',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlexBatchEditFormDialogComponent extends FlexFormDialogBaseComponent {

  formRecord: FlexTrade = undefined;

  fieldsToUpdate: string[] = [];
  tradeIds: string[] = [];

  constructor(cdref: ChangeDetectorRef,
              clearingFlexService: ClearingFlexService,
              messageService: MessageService,
              configService: AppConfigService,
              clearingTradeInfoService: ClearingTradeInfoService,
              commonFieldService: CommonFieldService,
              translateService: TranslateService,
              fieldsService: FieldsService,
              private dataLoadingService: ClearingFlexTradeDataLoadingService) {
    super(cdref,
      clearingFlexService,
      messageService,
      configService,
      clearingTradeInfoService,
      commonFieldService,
      translateService,
      fieldsService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.dataLoadingService.loadData(this.tracker);

    this.dialogFormTitle = this.getTranslation('page.' + this.transPartKey + '.dialog-title');


    this.formConfig = [
      {
        group: '',
        fields: [
          {
            field: 'ownerCode',
            label: 'Owner',
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.dataLoadingService.owners,
            required: true,
            hidden: () => this.dataLoadingService.owners.length == 1,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('ownerCode')
          },
          {
            field: 'exchangeCode',
            icon: (id: string) => {
              if (this.dataLoadingService.exchanges) {
                const exchField = this.dataLoadingService.exchanges.find(exchange => exchange.id === id);
                if (exchField && exchField.value)
                  return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
              }
              return 'fas fa-globe-europe';
            },
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.dataLoadingService.exchanges,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('exchangeCode')
          },
          {
            field: 'displayReference', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('displayReference')
          },
          {
            field: 'businessDate', column: 2, type: TYPE.DATE,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('businessDate')
          },
          {
            field: 'contractCode', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('contractCode')
          },
          {
            field: 'side',
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.dataLoadingService.sideOptions,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('side'),
            changed: (formRecords: any) => this.loadFooterInfos(this.tradeIds)
          },
          {
            field: 'type',
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.dataLoadingService.tradeTypes,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('type')
          },
          {
            field: 'maturity',
            column: 2,
            type: TYPE.TEXT,
            validate: (fTrade: FlexTrade) => !MaturityUtils.isMaturityValid(fTrade.maturity) ? 'Maturity is invalid (ddMMMyyyy,\'W\'WMMMyyyy,MMMyyyy)' : '',
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('maturity')
          },
          {
            field: 'volume', column: 2, type: TYPE.INTEGER,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('volume'),
            changed: (formRecords: any) => this.loadFooterInfos(this.tradeIds)
          },
          {
            field: 'price', column: 2, type: TYPE.DECIMAL,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('price'),
            changed: (formRecords: any) => this.loadFooterInfos(this.tradeIds)
          },
          {
            field: 'customOrderReference', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('customOrderReference')
          }
        ]
      },
      {
        group: 'details',
        // collapsed: true,
        fields: [
          {
            field: 'deleted', column: 2, type: TYPE.CHECKBOX,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('deleted')
          },
          // {field: 'account.name', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('account.name')},
          // {field: 'account.type', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('account.type')},
          {
            field: 'account.reference1', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('account.reference1')
          },
          // {field: 'account.reference2', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('account.reference2')},
          // {field: 'account.reference3', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('account.reference3')},
          // {field: 'avgPrice', column: 2, type: TYPE.DECIMAL,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('avgPrice')},
          // {field: 'avgPriceGroupId', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('avgPriceGroupId')},
          // {field: 'avgPriceResidual', column: 2, type: TYPE.DECIMAL,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('avgPriceResidual')},
          // {field: 'avgPriceResidualCurrency', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('avgPriceResidualCurrency')},
          // {field: 'avgRndPrice', column: 2, type: TYPE.DECIMAL,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('avgRndPrice')},
          {
            field: 'callPut', column: 2, type: TYPE.DROPDOWN, options: () => this.dataLoadingService.callPuts,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('callPut')
          },
          {
            field: 'clearingDate', column: 2, type: TYPE.DATE,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('clearingDate')
          },
          {
            field: 'contractUnit', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('contractUnit')
          },
          {
            field: 'exchangeReference', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('exchangeReference')
          },
          {
            field: 'executionDate', column: 2, type: TYPE.DATETIME,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('executionDate')
          },
          {
            field: 'openClose', column: 2, type: TYPE.DROPDOWN, options: () => this.dataLoadingService.openCloses,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('openClose')
          },
          {
            field: 'orderId', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('orderId')
          },
          {
            field: 'orderReference', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('orderReference')
          },
          {
            field: 'origineTrader', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('origineTrader')
          },
          {
            field: 'strike', column: 2, type: TYPE.DECIMAL,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('strike')
          },
          {
            field: 'trader', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('trader')
          },
          {
            field: 'tradingEnvironment', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('tradingEnvironment')
          },
          // {field: 'giveInMessage', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveInMessage')},
          // {field: 'giveInMessage2', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveInMessage2')},
          // {field: 'giveInMessage3', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveInMessage3')},
          {
            field: 'giveUpBrokerCode', column: 2, type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpBrokerCode')
          },
          // {field: 'giveUpCommission', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpCommission')},
          // {field: 'giveUpMessage', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveUpMessage')},
          // {field: 'giveupAccountType', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveupAccountType')},
          // {field: 'giveupGroupId', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveupGroupId')},
          // {field: 'giveupMessage2', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveupMessage2')},
          // {field: 'giveupMessage3', column: 2, type: TYPE.TEXT,
          //   labelCheckbox: true,
          //   labelCheckboxChanged: (event: FormFieldConfig, event2: any) => this.labelCheckboxChanged(event.field, event2.checked),
          //   disabled: (record: any) => !this.fieldsToUpdate.includes('giveupMessage3')} ,
          {
            column: 1,
            type: TYPE.NONE,
            validate: () => this.validate(),
          }
        ]
      },
    ];

  }

  save() {
    this.formLoading = true;
    this.markForCheck();
    const batchUpdateRequest = new BatchUpdateRequest<FlexTrade>();
    batchUpdateRequest.recordToApply = {...this.formRecord}
    batchUpdateRequest.recordIds = this.tradeIds;
    batchUpdateRequest.fieldToUpdate = [...this.fieldsToUpdate];
    batchUpdateRequest.recordToApply.account = new Account();
    batchUpdateRequest.recordToApply.account.name = this.formRecord['account.name'];
    batchUpdateRequest.recordToApply.account.type = this.formRecord['account.type'];
    batchUpdateRequest.recordToApply.account.reference1 = this.formRecord['account.reference1'];
    batchUpdateRequest.recordToApply.account.reference2 = this.formRecord['account.reference2'];
    batchUpdateRequest.recordToApply.account.reference3 = this.formRecord['account.reference3'];
    DateFormatPipe.checkDate(batchUpdateRequest.recordToApply, 'businessDate');
    DateFormatPipe.checkDate(batchUpdateRequest.recordToApply, 'clearingDate');
    DateFormatPipe.checkDate(batchUpdateRequest.recordToApply, 'executionDate');
    if (this.fieldsToUpdate.includes('exchangeCode') && batchUpdateRequest.recordToApply.exchangeCode) {
      batchUpdateRequest.recordToApply.clearingHouse = undefined;
      // batchUpdateRequest.recordToApply.clearingHouse = this.exchanges.find(exchange => exchange.id === this.formRecord.exchangeCode).value.clearingHouse;
    }

    this.formLoading = true;
    this.clearingFlexService.saveBatch(batchUpdateRequest).subscribe(validation => {
      if (validation.success) {
        this.fieldsToUpdate.splice(0)
        this.closeDialog();
        this.infoMessage(validation.message);
      } else {
        this.ccFormDialog.ccForm.errorMessage(validation.message);
      }
      this.formLoading = false;
    });
    return;
  }

  openDialog(trades: Trade[], ids: string[]): void {
    this.loadFooterInfos(ids);
    this.fieldsToUpdate.splice(0)
    if (this.ccFormDialog)
      this.ccFormDialog.reloadFieldValueMap();
    this.tradeIds = ids;
    this.formRecord = JSON.parse(JSON.stringify(trades[0]));
    this.formRecord.businessDate = new Date(trades[0].businessDate);
    this.formRecord.clearingDate = new Date(trades[0].clearingDate);
    this.formRecord.executionDate = new Date(trades[0].executionDate);
    this.formRecord.exchangeCode = this.formRecord.exchangeCode.replace(this.configService.flexGwSuffix, '');
    if (trades[0].account) {
      this.formRecord['account.name'] = trades[0].account.name;
      this.formRecord['account.type'] = trades[0].account.type;
      this.formRecord['account.reference1'] = trades[0].account.reference1;
      this.formRecord['account.reference2'] = trades[0].account.reference2;
      this.formRecord['account.reference3'] = trades[0].account.reference3;
    }
    this.dialogFormVisible = true;
    this.dialogFormVisibleChange.emit(true);
    this.markForCheck();
  }

  private labelCheckboxChanged(field: string, checked: boolean) {
    BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, field, checked)
    this.loadFooterInfos(this.tradeIds);
  }

  validate(): string {
    return this.fieldsToUpdate.length == 0 ? 'fail' : '';
  }

  protected recalculateSummaries() {
    if (this.fieldsToUpdate.includes('side')) {
      const summary = this.formRecord.side === Side.BUY ? this.summaryBuyValue : this.summarySellValue;
      const summaryOpposite = this.formRecord.side != Side.BUY ? this.summaryBuyValue : this.summarySellValue;
      summary.selectionCount = summary.selectionCount + summaryOpposite.selectionCount;
      summary.totalVolume = summary.totalVolume + summaryOpposite.totalVolume;
      summary.totalPrice = summary.totalPrice + summaryOpposite.totalPrice;
      summaryOpposite.selectionCount = 0;
      summaryOpposite.totalVolume = 0;
      summaryOpposite.totalPrice = 0;
    }
    if (this.fieldsToUpdate.includes('volume')) {
      this.summaryBuyValue.totalVolume = this.formRecord.volume * this.summaryBuyValue.selectionCount;
      this.summarySellValue.totalVolume = this.formRecord.volume * this.summarySellValue.selectionCount;
      this.summaryBuyValue.totalPrice = 0;
      this.summarySellValue.totalPrice = 0;
    }
    if (this.fieldsToUpdate.includes('price')) {
      this.summaryBuyValue.totalVolume = this.formRecord.volume * this.formRecord.price * this.summaryBuyValue.selectionCount;
      this.summarySellValue.totalVolume = this.formRecord.volume * this.formRecord.price * this.summarySellValue.selectionCount;
    }
  }
}
